import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { localServices } from "@/data/local-services";
import { bundeslaender, staedte, wienBezirke } from "@/data/locations";
import ctaLeistungen from "@/assets/schober/cta-leistungen.webp";
import { Testimonials } from "@/components/site/Testimonials";

export const Route = createFileRoute("/leistungen")({
  head: () => ({
    meta: [
      { title: "Leistungen: Umzug, Entrümpelung & Transport | Schober" },
      {
        name: "description",
        content:
          "Entrümpelung, Messie-Räumung, Umzug, Transport, Wohnungsauflösung, Firmen- & Büroumzug – in Wien, Niederösterreich und ganz Österreich. Angebot, versichert, schnell.",
      },
      { property: "og:title", content: "Unsere Leistungen – Schober Transportservice" },
      {
        property: "og:description",
        content: "7 Leistungen × über 180 Standorte in Österreich. Wählen Sie Ihre Region.",
      },
    ],
  }),
  component: LeistungenPage,
});

function LeistungenPage() {
  return (
    <>
      <PageHero title="LEISTUNGEN" subtitle="In ganz Österreich für Sie da" />

      {/* Services Grid */}
      <section className="container mx-auto max-w-6xl px-4 py-16 md:py-20">
        <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
          Unsere Leistungen
        </h2>
        <p className="mt-3 max-w-3xl text-foreground/80">
          Jede Leistung gibt es auch als Spezialseite für Ihre Stadt oder Ihren Wiener Bezirk –
          mit lokalen Infos, FAQ und Angebot.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {localServices.map((s) => (
            <Link
              key={s.slug}
              to="/leistungen/$service"
              params={{ service: s.slug }}
              className="group block overflow-hidden rounded-lg border border-border bg-background transition hover:border-brand-orange hover:shadow-md"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.titleLong}
                  width={1600}
                  height={900}
                  loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-bold text-brand-navy group-hover:text-brand-orange">
                  {s.titleLong}
                </h3>
                <p className="mt-2 text-sm text-foreground/75 line-clamp-3">{s.shortDescription}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-orange">
                  Mehr erfahren <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Standorte */}
      <section className="bg-muted py-16">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
            <MapPin className="inline h-7 w-7 text-brand-orange" /> Über {staedte.length + wienBezirke.length} Standorte in Österreich
          </h2>
          <p className="mt-3 max-w-3xl text-foreground/80">
            Wir sind in allen 9 Bundesländern, allen 23 Wiener Gemeindebezirken und in über{" "}
            {staedte.length} Städten mit mehr als 5.000 Einwohnern für Sie aktiv.
          </p>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {bundeslaender.map((bl) => (
              <div key={bl.slug} className="rounded-lg bg-background p-5 shadow-sm">
                <h3 className="font-display text-lg font-bold text-brand-navy">{bl.name}</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  {bl.population?.toLocaleString("de-AT")} Einwohner
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {localServices.slice(0, 3).map((s) => (
                    <Link
                      key={s.slug}
                      to="/leistungen/$service/$ort"
                      params={{ service: s.slug, ort: bl.slug }}
                      className="rounded-full bg-muted px-3 py-1 text-xs transition hover:bg-brand-orange hover:text-brand-orange-foreground"
                    >
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative bg-brand-navy text-black bg-top py-20 md:py-0 bg-cover bg-left bg-no-repeat"
        style={{ backgroundImage: `url(${ctaLeistungen})` }}
      >
        <div className="absolute bg-black/10 w-full h-full inset-0 z-0">&nbsp;</div>
        <div className="container mx-auto max-w-7xl grid grid-cols-1 md:grid-cols-2 gap-6 px-4 text-left md:text-right relative">
          <div></div>
          <div className="flex flex-col gap-6 items-center md:items-end px-10 md:px-0 py-10 md:py-20 z-20">
            <h2 className="font-display text-3xl md:text-2xl text-center md:text-right font-extrabold text-brand-orange md:text-4xl">
              Spezialprojekt? Wir finden eine Lösung.
            </h2>
            <p className="max-w-2xl text-black md:text-white/80 text-center md:text-right">
              Setzen Sie sich mit uns in Verbindung – wir erstellen Ihnen ein maßgeschneidertes Angebot.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground transition-all hover:scale-105"
            >
              Kontakt aufnehmen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
      <Testimonials />
    </>
  );
}
